import dayjs, { Dayjs } from 'dayjs';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { useOnlineStatus } from '../../utilities/useOnlineStatus';
import { useAIMSHeartbeat } from '../../utilities/hooks/useAIMSHeartbeat';
import { useEventTracking } from '../../utilities/hooks/useEventTracking';
import { useFeatureFlags } from '../../utilities/useFeatureFlag';
import type { FeatureFlags } from '../../types/featureFlags';

const HEART_BEAT_SEND_INTERVAL_MINUTES = 15;

const AIMSHeartbeat: React.FC = observer(() => {
  const { sendAIMSHeartbeat } = useAIMSHeartbeat();
  const location = useLocation();
  const isOnline = useOnlineStatus();
  const { sendCachedEvents } = useEventTracking();
  const featureFlag: FeatureFlags = useFeatureFlags();
  const [aimsDataSync, setAimsDataSync] = useState({ message: '', allowSync: false });

  const lastUserInteractionTime = React.useRef<Dayjs>(dayjs());

  const blackListedPaths = [
    '/',
    '/login',
    '/logout',
    '/signup',
    '/forgot-password',
    '/home',
    '/initializing',
  ];

  const sendAIMSHeartbeatIfValid = async () => {
    if (blackListedPaths.includes(location.pathname) || !aimsDataSync?.allowSync) return;
    try {
      sendAIMSHeartbeat({ lastUserInteractionTime: lastUserInteractionTime.current });
    } catch (e) {
      console.log('🚀 ~ file: AIMSHeartbeat.tsx:24 ~ sendAIMSHeartbeatIfValid ~ e:', e);
    }
  };

  useEffect(() => {
    if (isOnline && aimsDataSync?.allowSync) sendCachedEvents().then(sendAIMSHeartbeatIfValid);
    else sendAIMSHeartbeatIfValid();

    // Send Heartbeat every 15 minutes
    const interval = setInterval(() => {
      sendAIMSHeartbeatIfValid();
    }, HEART_BEAT_SEND_INTERVAL_MINUTES * 60 * 1000);

    return () => clearInterval(interval);
  }, [isOnline, aimsDataSync?.allowSync]);

  // Track user interactions
  useEffect(() => {
    window.addEventListener(
      'touchstart',
      () => {
        lastUserInteractionTime.current = dayjs();
      },
      { passive: true },
    );
  }, []);

  useEffect(() => {
    if (featureFlag && featureFlag?.aimsDataSyncIsActive) {
      const { aimsDataSyncIsActive, aimsDataSyncMessage } = featureFlag;
      setAimsDataSync({
        message: String(aimsDataSyncMessage ?? ''),
        allowSync: aimsDataSyncIsActive as boolean,
      });
    }
  }, [featureFlag]);

  return <></>;
});

export default AIMSHeartbeat;
